import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography } from '@mui/material';
import ShoppingPreferencesForm, { ShoppingPreferencesFormValues } from './common/Forms/ShoppingPreferencesForm';
import ProgressButtons from './common/Buttons/ProgressButtons';
import { UpdatePartialProfile } from '../services/profile/ProfileService';
import { formatDateToISO } from './common/FieldFormat';
import { ProfileInfo } from './common/props-types';
import { Gender } from 'services/profile/constants';
import GoogleTagManager from 'common/GoogleTagManager';

const ShoppingPreferences = (props: {
  profileAuth: { sessionId: string; token: string };
  stepHandler: (value: number | ((prevVar: number) => number)) => void;
  currentStep: number;
  profileHandler: (value: ProfileInfo | ((prevVar: ProfileInfo) => ProfileInfo)) => void;
  profile: ProfileInfo;
}): JSX.Element => {
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { t } = useTranslation('component');
  const isFamiliplusMember =
    props.profile.familiplusCard != undefined && props.profile.familiplusCard.trim().length >= 10;

  const initialValues: ShoppingPreferencesFormValues = {
    InvoicePreference: props.profile.ereceiptPreference || 0,
    GenderPreference: props.profile.gender || props.profile.gender == 0 ? props.profile.gender : Gender.Unknown,
    BirthdayDate: props.profile.birthday == undefined ? null : formatDateToISO(props.profile.birthday),
  };

  const handleSave = async function (values: ShoppingPreferencesFormValues) {
    if (!isLoading) {
      setIsLoading(true);
      setErrorMessage('');
      try {
        //1 = email, 2 = paper, 3 = both
        const invoicePreference = +(values['InvoicePreference'] ?? 0);
        //0 = Male, 1 = Female, 2 = Unknown, 3 = Other
        const genderPreference =
          values.GenderPreference || +values.GenderPreference == 0 ? +values.GenderPreference : Gender.Unknown;

        let birthday;
        if (values.BirthdayDate) {
          const dateValidate = Date.parse(values.BirthdayDate);
          //Il faut ajouter T00:00:00 pour que la date sois la bonne, sinon sa prend la veille car il utilise le timezone UTC
          birthday = isNaN(dateValidate) ? undefined : new Date(values.BirthdayDate + 'T00:00:00');
        }

        if (
          props.profile.ereceiptPreference == invoicePreference &&
          props.profile.gender == genderPreference &&
          props.profile.birthday == birthday &&
          !birthday &&
          invoicePreference == 0 &&
          genderPreference == Gender.Unknown
        ) {
          GoogleTagManager.sendMemberShopping(genderPreference, birthday);
          props.stepHandler(props.currentStep + 1);
        } else {
          props.profile.ereceiptPreference = invoicePreference;
          props.profile.gender = genderPreference;
          props.profile.birthday = birthday;

          props.profileHandler((prevProfile) => {
            return { ...prevProfile, ...props.profile };
          });

          const updatePromises = [];
          if (birthday || invoicePreference != 0 || genderPreference != Gender.Unknown) {
            updatePromises.push([UpdatePartialProfile(props.profileAuth.sessionId, props.profile)]);
          }

          await Promise.all(updatePromises);
          GoogleTagManager.sendMemberShopping(genderPreference, birthday);
          props.stepHandler(props.currentStep + 1);
        }
      } catch (e) {
        setErrorMessage(t('Errors.GenericError') as string);
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <>
      <Typography variant="h1" sx={{ mb: '16px' }}>
        {t('ShoppingPreferencesForm.Title')}
      </Typography>

      <ShoppingPreferencesForm
        isFamiliplusMember={isFamiliplusMember}
        shoppingPreferences={initialValues}
        saveHandler={handleSave}
        saveErrorMessage={errorMessage}
        footer={
          <ProgressButtons
            stepHandler={props.stepHandler}
            currentStep={props.currentStep}
            hasPrevious={true}
            hasSkip={false}
            customSubmitText={null}
            isLoading={isLoading}
          />
        }
        showPtsBox={props.profile.familiplusCard != undefined && props.profile.familiplusCard?.length > 0}
      />
    </>
  );
};

export default ShoppingPreferences;
